// A text's Parameters (#241 part 2), in live's order: Transform, Content, Typography, Text
// Color, Text Background, Opacity, Stroke, Shadow and Animations (#266, `AnimationsSection`), with this
// app's Timing and Remove. Every change goes through the timeline (`styled` holds the ranges).
import { Button, Select, Text, TextInput } from "@mantine/core";
import { RotateCw, Strikethrough, Trash2, Underline } from "lucide-react";

import type { FontFamily } from "../../api/generated/model";
import { textClipTextMax } from "../../api/generated/zod";

import { AnimationsSection } from "./AnimationsSection";
import classes from "./editor.module.css";
import { FONT_FAMILIES, stylesOf } from "./fonts";
import { ADDED_SHADOW, ADDED_STROKE } from "./look";
import {
  ALIGNS,
  BackgroundFields,
  CASES,
  ColorField,
  ExtraSection,
  LevelSlider,
  LookNumber,
  LookRow,
  ParamSection,
  SecondsField,
  ShadowFields,
  StrokeFields,
  Toggle,
} from "./params";
import {
  ADDED_BACKGROUND,
  ADDED_TEXT_COLOR,
  MIN_TEXT_SIZE,
  type TextStylePatch,
} from "./text-style";
import { MIN_SPAN_MS, type TextClip, type TimelineEdit } from "./timeline";

export interface TextSettingsProps {
  text: TextClip;
  onEdit: (edit: TimelineEdit) => void;
}

/**
 * Live's Parameters for a text. Transform's numbers are live's pixels on its frame; the height
 * is the text's own (live's H field takes no value), so it shows "Auto".
 */
export function TextSettings({ text, onEdit }: TextSettingsProps) {
  const id = text.id;
  const style = text.style;
  const set = (patch: TextStylePatch) => onEdit({ type: "setClip", id, patch: { style: patch } });
  const styles = stylesOf(style.font);
  const current = styles.find((each) => each.weight === style.weight && each.italic === style.italic);
  return (
    <section className={classes.inspector} aria-label="Text settings">
      <div className={classes.paramHead}>
        <Text className={classes.paramTitle} truncate>
          Text: {text.text}
        </Text>
      </div>
      <ParamSection title="Timing">
        <SecondsField
          label="Start"
          ms={text.startMs}
          min={0}
          max={text.endMs - MIN_SPAN_MS}
          onCommit={(startMs) => onEdit({ type: "trimStart", id, startMs })}
        />
        <SecondsField
          label="End"
          ms={text.endMs}
          min={text.startMs + MIN_SPAN_MS}
          onCommit={(endMs) => onEdit({ type: "trimEnd", id, endMs, sourceMs: null })}
        />
      </ParamSection>
      <ParamSection title="Transform">
        <LookRow label="Position">
          <LookNumber label="Position X" tag="X" value={style.x} onCommit={(x) => set({ x })} />
          <LookNumber label="Position Y" tag="Y" value={style.y} onCommit={(y) => set({ y })} />
        </LookRow>
        <LookRow label="Size">
          <LookNumber label="Size W" tag="W" value={style.width} min={1} onCommit={(width) => set({ width })} />
          <TextInput
            aria-label="Size H"
            classNames={{ input: classes.lookInput, section: classes.lookTag }}
            leftSection="H"
            value="Auto"
            readOnly
            disabled
          />
        </LookRow>
        <LookRow label="Rotation">
          <LookNumber
            label="Rotation"
            tag={<RotateCw size={12} aria-hidden />}
            value={style.rotation}
            onCommit={(rotation) => set({ rotation })}
          />
        </LookRow>
      </ParamSection>
      <ParamSection title="Content">
        <TextInput
          aria-label="Text content"
          placeholder="Text content"
          classNames={{ input: classes.lookInput }}
          value={text.text}
          maxLength={textClipTextMax}
          error={text.text.trim() === "" ? "Write the text." : undefined}
          onChange={(event) => onEdit({ type: "setClip", id, patch: { text: event.currentTarget.value } })}
        />
      </ParamSection>
      <ParamSection title="Typography">
        <Select
          aria-label="Font"
          placeholder="Search fonts..."
          classNames={{ input: classes.lookInput }}
          searchable
          nothingFoundMessage="No fonts found"
          allowDeselect={false}
          data={FONT_FAMILIES.map((family) => ({ value: family, label: family }))}
          value={style.font}
          onChange={(family) => {
            if (family !== null) set({ font: family as FontFamily });
          }}
        />
        <LookRow label="Style">
          <Select
            aria-label="Style"
            classNames={{ input: classes.lookInput }}
            allowDeselect={false}
            data={styles.map((each) => ({ value: each.label, label: each.label }))}
            value={current?.label ?? null}
            onChange={(label) => {
              const chosen = styles.find((each) => each.label === label);
              if (chosen !== undefined) set({ weight: chosen.weight, italic: chosen.italic });
            }}
          />
          <LookNumber label="Size" tag="Aa" value={style.size} min={MIN_TEXT_SIZE} onCommit={(size) => set({ size })} />
        </LookRow>
        <LookRow label="Align">
          {ALIGNS.map((each) => (
            <Toggle
              key={each.value}
              label={each.label}
              on={style.align === each.value}
              onClick={() => set({ align: each.value })}
            >
              {each.icon}
            </Toggle>
          ))}
        </LookRow>
        <LookRow label="Decoration">
          <Toggle label="Underline" on={style.underline} onClick={() => set({ underline: !style.underline })}>
            <Underline size={14} />
          </Toggle>
          <Toggle label="Overline" on={style.overline} onClick={() => set({ overline: !style.overline })}>
            <span className={classes.overlineMark}>O</span>
          </Toggle>
          <Toggle
            label="Strikethrough"
            on={style.strikethrough}
            onClick={() => set({ strikethrough: !style.strikethrough })}
          >
            <Strikethrough size={14} />
          </Toggle>
        </LookRow>
        <LookRow label="Case">
          {CASES.map((each) => (
            <Toggle
              key={each.value}
              label={each.label}
              on={style.textCase === each.value}
              onClick={() => set({ textCase: each.value })}
            >
              {each.icon}
            </Toggle>
          ))}
        </LookRow>
      </ParamSection>
      <ExtraSection
        title="Text Color"
        open={style.color !== null}
        onAdd={() => set({ color: ADDED_TEXT_COLOR })}
        onRemove={() => set({ color: null })}
      >
        {style.color === null ? null : (
          <LookRow label="Color">
            <ColorField label="Text color" value={style.color} onCommit={(color) => set({ color })} />
          </LookRow>
        )}
      </ExtraSection>
      <ExtraSection
        title="Text Background"
        open={style.background !== null}
        onAdd={() => set({ background: ADDED_BACKGROUND })}
        onRemove={() => set({ background: null })}
      >
        {style.background === null ? null : (
          <BackgroundFields background={style.background} onChange={(background) => set({ background })} />
        )}
      </ExtraSection>
      <ParamSection title="Opacity">
        <LevelSlider
          label="Opacity"
          bare
          value={Math.round(style.opacity * 100)}
          max={100}
          unit="%"
          onChange={(percent) => set({ opacity: percent / 100 })}
        />
      </ParamSection>
      <ExtraSection
        title="Stroke"
        open={style.stroke !== null}
        onAdd={() => set({ stroke: ADDED_STROKE })}
        onRemove={() => set({ stroke: null })}
      >
        {style.stroke === null ? null : <StrokeFields stroke={style.stroke} onChange={(stroke) => set({ stroke })} />}
      </ExtraSection>
      <ExtraSection
        title="Shadow"
        open={style.shadow !== null}
        onAdd={() => set({ shadow: ADDED_SHADOW })}
        onRemove={() => set({ shadow: null })}
      >
        {style.shadow === null ? null : <ShadowFields shadow={style.shadow} onChange={(shadow) => set({ shadow })} />}
      </ExtraSection>
      <AnimationsSection clip={text} onEdit={onEdit} />
      <Button
        size="xs"
        variant="subtle"
        color="red"
        mt="md"
        leftSection={<Trash2 size={14} />}
        onClick={() => onEdit({ type: "removeClip", id })}
      >
        Remove text
      </Button>
    </section>
  );
}
